import React, { useState, useEffect } from 'react';

const sections = [
  { id: 'about',      label: 'About' },
  { id: 'techstack',  label: 'Stack' },
  { id: 'experience', label: 'Experience' },
  { id: 'education',  label: 'Education' },
  { id: 'opensource', label: 'Open Source' },
  { id: 'projects',   label: 'Projects' },
  { id: 'profiles',   label: 'Profiles' },
  { id: 'writings',   label: 'Writings' },
  { id: 'contact',    label: 'Contact' },
];

const socials = [
  { label: 'GH',  title: 'GitHub',   href: 'https://github.com/Krishna41357' },
  { label: 'LI',  title: 'LinkedIn', href: 'https://linkedin.com/in/krishna-srivastava-a84b65299' },
  { label: 'X',   title: 'Twitter',  href: 'https://x.com/Krishna91135239' },
];

export default function Sidebar() {
  const [active, setActive] = useState('about');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const observers = [];
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { rootMargin: '-20% 0px -65% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    const onScroll = () => {
      const scrolled = window.scrollY;
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.round((scrolled / total) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observers.forEach((o) => o.disconnect());
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const activeIndex = sections.findIndex((s) => s.id === active);

  return (
    <aside
      className="fixed left-0 top-0 h-full w-56 flex flex-col z-40 select-none"
      style={{ background: '#F0EEE8' }}
    >
      {/* Right border — hard line */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-black/12" />

      {/* Subtle grid overlay on sidebar too */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* ── Top: identity ── */}
      <div className="relative px-5 pt-7 pb-5 border-b border-black/8">
        {/* Monogram */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-purple-600 rounded-lg border-2 border-black flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] shrink-0">
            <span className="text-white font-black text-xs tracking-tight">KS</span>
          </div>
          <div>
            <p className="text-black text-xs font-black leading-tight tracking-tight">Krishna S.</p>
            <p className="text-purple-600 text-[9px] font-mono leading-tight mt-0.5">Full Stack Dev</p>
          </div>
        </div>

        {/* Status pill */}
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-black/15 rounded-full shadow-[1px_1px_0px_0px_rgba(0,0,0,0.3)]">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[9px] font-mono text-green-700 uppercase tracking-widest font-bold">Open to work</span>
        </div>
      </div>

      {/* ── Navigation ── */}
      <div className="relative flex-1 px-3 py-4 overflow-y-auto overflow-x-hidden">
        {/* Counter */}
        <div className="flex items-center justify-between px-2 mb-3">
          <span className="text-[9px] font-mono text-black/30 uppercase tracking-widest">Navigate</span>
          <span className="text-[9px] font-mono text-purple-600 font-bold">
            {String(activeIndex + 1).padStart(2, '0')}/{String(sections.length).padStart(2, '0')}
          </span>
        </div>

        <nav className="space-y-0.5">
          {sections.map(({ id, label }, i) => {
            const isActive = active === id;
            return (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`group relative w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-all duration-200 ${
                  isActive ? 'bg-white shadow-[2px_2px_0px_0px_rgba(0,0,0,0.12)]' : 'hover:bg-white/60'
                }`}
              >
                {/* Active left bar */}
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-purple-600 rounded-r" />
                )}

                {/* Number */}
                <span className={`text-[9px] font-mono w-5 shrink-0 tabular-nums ${
                  isActive ? 'text-purple-600 font-bold' : 'text-black/30 group-hover:text-black/50'
                }`}>
                  {String(i + 1).padStart(2, '0')}
                </span>

                {/* Label */}
                <span className={`text-[12px] flex-1 truncate transition-colors ${
                  isActive ? 'font-bold text-black' : 'font-medium text-black/50 group-hover:text-black/80'
                }`}>
                  {label}
                </span>

                {/* Active dot */}
                {isActive && (
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-600 shrink-0" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Progress ── */}
      <div className="relative px-5 py-3 border-t border-black/8">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] font-mono text-black/30 uppercase tracking-widest">Scroll</span>
          <span className="text-[9px] font-mono text-black/50 font-medium">{progress}%</span>
        </div>
        <div className="w-full h-px bg-black/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-600 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* ── Socials ── */}
      <div className="relative px-5 pb-6 pt-3 border-t border-black/8">
        <p className="text-[9px] font-mono text-black/30 uppercase tracking-widest mb-3">Connect</p>
        <div className="flex items-center gap-2">
          {socials.map(({ label, title, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={title}
              className="flex items-center justify-center w-8 h-8 bg-white hover:bg-purple-600 border-2 border-black rounded-lg text-black/60 hover:text-white transition-all duration-200 text-[10px] font-black font-mono shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
            >
              {label}
            </a>
          ))}
        </div>
      </div>
    </aside>
  );
}
